<template>
  <EditorModal
    :title="t('campaignBulk.workflowSelector.title')"
    :show-close-button="true"
    @on-close-modal="closeModal"
  >
    <template #content>
      <div class="campaign-selector-layout">
        <!-- Left Sidebar Filters -->
        <div class="filters-sidebar">
          <div class="filters-sidebar__content">
            <!-- Search -->
            <div class="filter-section">
              <div class="search-wrapper">
                <i class="fa fa-search search-icon" />
                <input
                  v-model="searchQuery"
                  type="text"
                  class="search-input"
                  :placeholder="t('campaignBulk.workflowSelector.search.placeholder')"
                >
                <div
                  v-if="searchQuery"
                  class="clear-search-btn"
                  @click="searchQuery = ''"
                >
                  <i class="fa fa-times" />
                </div>
              </div>
            </div>

            <!-- Channel Tags Filter -->
            <div
              v-if="channelTagGroup.length > 0"
              class="filter-section filter-section--tags"
            >
              <label class="filter-section__label filter-section__label--small">
                <i class="fa-light fa-tower-broadcast" />
                {{ t('campaignBulk.workflowSelector.filters.channelTags') }}
              </label>
              <div class="filter-tags">
                <div
                  v-for="tag in channelTagGroup"
                  :key="tag.value"
                  class="filter-tag filter-tag--channel"
                  :class="{ 'filter-tag--active': selectedTags.includes(tag.value) }"
                  @click="toggleTag(tag.value)"
                >
                  <span class="filter-tag__label">{{ tag.value }}</span>
                  <span class="filter-tag__count">{{ tag.count }}</span>
                </div>
              </div>
            </div>

            <!-- Author Filter — visual cards, same UX as the automation
                 picker. Pulls split + search from the shared composable. -->
            <div class="filter-section filter-section--authors">
              <label class="filter-section__label">
                <i class="fa-light fa-user" />
                {{ t('campaignBulk.workflowSelector.filters.author') }}
              </label>

              <!-- Inline search once the list is long. -->
              <div
                v-if="authorFilterOptions.length > 6"
                class="author-list__search"
              >
                <i class="fa-light fa-search" />
                <input
                  v-model="authorSearchQuery"
                  type="text"
                  :placeholder="t('automatedScenarios.workflowSelector.filters.searchAuthor')"
                >
              </div>

              <!-- Unified author list: ShopiMind + integrations, same level. -->
              <div class="author-list">
                <div
                  v-for="option in filteredAuthorOptions"
                  :key="option.value"
                  class="author-list__item"
                  :class="{
                    'author-list__item--integration': option.value !== 'shopimind' && option.value !== 'me',
                    'author-list__item--active': selectedAuthors.includes(option.value),
                  }"
                  @click="toggleAuthor(option.value)"
                >
                  <span class="author-list__avatar">
                    <img
                      v-if="getAuthorIcon(option.value)"
                      :src="getAuthorIcon(option.value)"
                      :alt="option.label"
                    >
                    <i
                      v-else
                      :class="option.value === 'shopimind' ? 'fa-light fa-user' : 'fa-solid fa-plug'"
                    />
                  </span>
                  <span class="author-list__label">{{ option.label }}</span>
                  <span
                    v-if="option.count !== undefined"
                    class="author-list__count"
                  >{{ option.count }}</span>
                  <span
                    v-if="selectedAuthors.includes(option.value)"
                    class="author-list__check"
                  >
                    <i class="fa-solid fa-check" />
                  </span>
                </div>
                <div
                  v-if="filteredAuthorOptions.length === 0"
                  class="author-list__empty"
                >
                  {{ t('automatedScenarios.workflowSelector.filters.noAuthorMatch') }}
                </div>
              </div>
            </div>

            <!-- Results count -->
            <div class="filter-results">
              <span class="filter-results__count">{{ filteredList.length }}</span>
              <span class="filter-results__label">{{ t('campaignBulk.workflowSelector.filters.results') }}</span>
            </div>
          </div>
        </div>

        <!-- Campaigns Grid -->
        <main class="campaigns-content">
          <!-- Drill-in banner — shown when filtered to a single integration (G10). -->
          <DrilldownBanner
            v-if="drilledInIntegration"
            :name="drilledInIntegration.name"
            :icon="drilledInIntegration.icon"
            @back="clearAuthorFilter"
          />

          <Loader v-if="isListLoading" />

          <div
            v-else
            class="campaigns-grid"
          >
            <!-- Start from scratch card — hidden when drilled into an integration. -->
            <div v-if="!drilledInIntegration" class="campaign-card campaign-card--scratch">
              <div class="campaign-card__content">
                <div class="campaign-card__image campaign-card__image--scratch">
                  <div class="scratch-icon-wrapper">
                    <i class="fa-light fa-plus" />
                  </div>
                </div>
                <div class="campaign-card__body">
                  <h3 class="campaign-card__title">
                    {{ t('campaignBulk.workflowSelector.startFromScratch.title') }}
                  </h3>
                </div>

                <!-- Hover overlay -->
                <div class="campaign-card__hover-overlay">
                  <p class="campaign-card__hover-legend">
                    {{ t('campaignBulk.workflowSelector.startFromScratch.description') }}
                  </p>
                  <div class="campaign-card__hover-actions">
                    <Button
                      :label="t('campaignBulk.workflowSelector.actions.choose')"
                      class="p-button-sm p-button-success choose-btn"
                      @click="handleStartFromScratch"
                    />
                  </div>
                </div>
              </div>
            </div>

            <!-- Campaign cards from API (+ integration collection cards) -->
            <template
              v-for="record of displayItems"
              :key="record.__collection ? ('coll-' + record.slug) : record.id"
            >
              <IntegrationCollectionCard
                v-if="record.__collection"
                :name="record.name"
                :icon="record.icon"
                :count="record.count"
                :status="record.status"
                :previews="record.previews"
                :kicker="t('automatedScenarios.workflowSelector.byPartner')"
                :count-label="t('integrations.collection.count', { count: record.count })"
                :open-label="t('integrations.collection.open', { count: record.count })"
                :install-label="t('integrations.collection.install')"
                @open="drillIntoIntegration(record.slug)"
              />
              <div
                v-else
                class="campaign-card"
                :class="{ 'campaign-card--integration': record.author === 'integration' }"
              >
                <div class="campaign-card__content">
                <!-- Partner ribbon, overhanging the top-left corner.
                     Same pattern as CampaignAutomationSelectorV2. -->
                <div
                  v-if="record.author === 'integration' && record.integration_name && !drilledInIntegration"
                  class="campaign-card__partner-ribbon"
                >
                  <div class="campaign-card__partner-logo">
                    <img
                      v-if="record.integration_icon_url"
                      :src="record.integration_icon_url"
                      :alt="record.integration_name"
                    >
                    <i
                      v-else
                      class="fa-solid fa-plug"
                    />
                  </div>
                  <div class="campaign-card__partner-text">
                    <span class="campaign-card__partner-kicker">
                      {{ t('automatedScenarios.workflowSelector.byPartner') }}
                    </span>
                    <span class="campaign-card__partner-name">{{ record.integration_name }}</span>
                  </div>
                </div>

                <!-- Image Preview -->
                <div class="campaign-card__image">
                  <div
                    v-if="record.images_templates && record.images_templates.length > 0"
                    class="campaign-card__preview"
                    :style="`background-image: url(${record.images_templates[0]})`"
                  />
                  <div
                    v-else
                    class="campaign-card__preview campaign-card__preview--placeholder"
                  >
                    <i class="fa fa-image" />
                  </div>
                </div>

                <!-- Default: title + author -->
                <div class="campaign-card__body">
                  <h3 class="campaign-card__title">
                    {{ getLocalizedText(record.name, 'Sans titre') }}
                  </h3>
                  <div
                    v-if="record.author !== 'integration'"
                    class="campaign-card__author"
                  >
                    <i class="fa-light fa-user" />
                    <span>ShopiMind</span>
                  </div>
                </div>

                <!-- Hover overlay: description + tags + actions -->
                <div class="campaign-card__hover-overlay">
                  <p
                    v-if="getLocalizedText(record.legend)"
                    class="campaign-card__hover-legend"
                  >
                    {{ getLocalizedText(record.legend) }}
                  </p>
                  <div
                    v-if="record.tags && record.tags.length"
                    class="campaign-card__hover-tags"
                  >
                    <span
                      v-for="tag in record.tags"
                      :key="tag"
                      class="tag-badge tag-badge--channel"
                    >
                      {{ tag }}
                    </span>
                  </div>
                  <div class="campaign-card__hover-actions">
                    <Button
                      class="p-button-sm p-button-outlined preview-btn"
                      @click.stop="openGalleryHtmlPreview(record)"
                    >
                      <i class="fa-light fa-eye" />
                    </Button>
                    <Button
                      :label="t('campaignBulk.workflowSelector.actions.choose')"
                      class="p-button-sm p-button-success choose-btn"
                      @click="handleChoose(record)"
                    />
                  </div>
                </div>
              </div>
            </div>
            </template>
          </div>
        </main>
      </div>
    </template>
  </EditorModal>

  <TemplateHtmlPreviewSlider
    v-if="htmlPreviewData && htmlPreviewData.previews.length > 0"
    :previews="htmlPreviewData.previews"
    @close="closeHtmlPreview"
  />

  <RequireIntegrationDialog
    v-model:visible="requireIntegrationVisible"
    :integration="requireIntegrationData"
    :status-label="requireIntegrationStatus"
  />
</template>

<script lang="ts">
import Button from 'primevue/button';
import MultiSelect from 'primevue/multiselect';
import {
  defineComponent, onMounted, Ref, ref, SetupContext, computed,
} from 'vue';
import { useI18n } from 'vue-i18n';
import { CampaignsBulkStatusEnum } from '@/types';
import EditorModal from '@/components/modals/EditorModal.vue';
import Loader from '@/components/layout/Loader.vue';
import { asInt, showToastError, showToastSuccess } from '@/helpers';
import { setRefreshScenariosList } from '@/composables/AutomatedScenarios';
import { getNowFormattedByTimezone } from '@/helpers/Date';
import { openBulkCampaignEditor } from '@/views/workflows/BulkCampaignsActions/useBulkCampaignsActions';
import TemplateHtmlPreviewSlider from '@/components/import/TemplateHtmlPreviewSlider.vue';
import { store } from '@/store';
import { getCampaignsBulkBaseList } from '@/composables/workflows/CampaignBulk';
import { nestCall, NestApiType } from '@/composables/nestApi';
import { UserState } from '@/composables/User';
import RequireIntegrationDialog from '@/components/integrations/RequireIntegrationDialog.vue';
import type { IntegrationOwnedItem } from '@/types/integrations';
import { useIntegrationGating } from '@/composables/integrations/useIntegrationGating';
import { useIntegrationCollections } from '@/composables/integrations/useIntegrationCollections';
import DrilldownBanner from '@/components/integrations/DrilldownBanner.vue';
import { pickLocalizedText } from '@/utils/localized-text';
import IntegrationCollectionCard from '@/components/integrations/IntegrationCollectionCard.vue';

interface CampaignsBulkBase extends IntegrationOwnedItem {
  id: number;
  name: Record<string, string>;
  legend: Record<string, string>;
  tags: string[];
  categories: string;
  path: string;
  images_templates: string[] | null;
}

const CHANNEL_TAGS = ['Email', 'SMS', 'Push', 'WhatsApp', 'Popup', 'Cross-canal'];

export default defineComponent({
  name: 'CampaignsBulkBaseSelectorV2',

  components: {
    TemplateHtmlPreviewSlider,
    Loader,
    EditorModal,
    Button,
    MultiSelect,
    RequireIntegrationDialog,
    IntegrationCollectionCard,
    DrilldownBanner,
  },

  emits: {
    'on-close-dialog': null,
  },

  setup(props: { }, context: SetupContext) {
    const { t, locale } = useI18n();

    const currentLocale = computed(() => locale.value || 'fr');

    const getLocalizedText = (textObj: Record<string, string> | undefined, fallback = ''): string => {
      if (!textObj || typeof textObj !== 'object') return fallback;
      return pickLocalizedText(textObj, currentLocale.value) || fallback;
    };

    const isListLoading = ref(false);
    const list: Ref<CampaignsBulkBase[]> = ref([]);
    const htmlPreviewData = ref<{ previews: { html: string; label: string }[] } | null>(null);
    const searchQuery = ref('');

    // Integration gating shared with the automation/template pickers via
    // `useIntegrationGating`.
    const selectedAuthors = ref<string[]>([]);
    const {
      requireIntegrationVisible,
      requireIntegrationData,
      requireIntegrationStatus,
      gatePreflight,
      gateBackendResponse,
      authorFilterOptionsFor,
    } = useIntegrationGating();

    const authorFilterOptions = authorFilterOptionsFor(() => list.value);

    // ----- Visual author filter (same UX as the automation picker) -----

    const authorSearchQuery = ref('');

    /** All author options (ShopiMind + integrations at the same level),
     *  filtered by the search input. Integration options carry a `count`. */
    const filteredAuthorOptions = computed(() => {
      const q = authorSearchQuery.value.trim().toLowerCase();
      if (!q) return authorFilterOptions.value;
      return authorFilterOptions.value.filter(
        (o) => o.label.toLowerCase().includes(q)
          || o.value.toLowerCase().includes(q),
      );
    });

    const toggleAuthor = (value: string) => {
      const idx = selectedAuthors.value.indexOf(value);
      if (idx >= 0) {
        selectedAuthors.value.splice(idx, 1);
      } else {
        selectedAuthors.value.push(value);
      }
    };

    const getAuthorIcon = (slug: string): string | undefined => {
      if (slug === 'shopimind') return undefined;
      const match = list.value.find((item) => item.integration_slug === slug);
      return match?.integration_icon_url ?? undefined;
    };

    // Tag filter
    const selectedTags = ref<string[]>([]);

    const channelTagGroup = computed(() => {
      const selected = selectedTags.value;
      const tagCounts = new Map<string, number>();

      for (const item of list.value) {
        const itemTags = item.tags || [];
        if (!selected.every((t) => itemTags.includes(t))) continue;
        for (const tag of itemTags) {
          if (CHANNEL_TAGS.includes(tag)) {
            tagCounts.set(tag, (tagCounts.get(tag) || 0) + 1);
          }
        }
      }

      const tags: { value: string; count: number }[] = [];
      for (const [value, count] of tagCounts) {
        tags.push({ value, count });
      }
      // Sort by CHANNEL_TAGS order
      tags.sort((a, b) => CHANNEL_TAGS.indexOf(a.value) - CHANNEL_TAGS.indexOf(b.value));

      return tags;
    });

    const toggleTag = (tag: string) => {
      const idx = selectedTags.value.indexOf(tag);
      if (idx >= 0) {
        selectedTags.value.splice(idx, 1);
      } else {
        selectedTags.value.push(tag);
      }
    };

    const filteredList = computed(() => {
      let result = list.value;

      // Filter by selected tags (AND: all selected tags must be present)
      if (selectedTags.value.length > 0) {
        result = result.filter((campaign) => {
          const tags = campaign.tags || [];
          return selectedTags.value.every((tag) => tags.includes(tag));
        });
      }

      // Filter by search query
      if (searchQuery.value.trim()) {
        const query = searchQuery.value.toLowerCase();
        result = result.filter((campaign) => getLocalizedText(campaign.name).toLowerCase().includes(query)
            || getLocalizedText(campaign.legend).toLowerCase().includes(query));
      }

      // Filter by author. Empty selection = no constraint. Otherwise we
      // keep items whose author key (slug or 'shopimind') is in the set.
      if (selectedAuthors.value.length > 0) {
        result = result.filter((campaign) => {
          const authorKey = campaign.author === 'integration'
            ? campaign.integration_slug ?? ''
            : 'shopimind';
          return selectedAuthors.value.includes(authorKey);
        });
      }

      return result;
    });

    const closeModal = () => {
      context.emit('on-close-dialog');
    };

    const handleStartFromScratch = async () => {
      store.commit('general/showTheSpinner');

      try {
        const idShop = UserState.activeShop?.id ?? 0;

        if (!idShop) {
          showToastError(t('campaignBulk.create_action.error'));
          store.commit('general/hideTheSpinner');
          return;
        }

        // Generate campaign name with current date
        const name = `${t('campaignPrefix')} - ${getNowFormattedByTimezone()}`;

        // Create empty bulk campaign via API
        const result = await nestCall(
          'post',
          NestApiType.CLIENT_API,
          '/campaigns/bulk',
          {},
          '',
          { name },
        );

        if (result && result.data && result.data.id) {
          // Use shared function to open the editor with all checks
          await openBulkCampaignEditor(
            result.data.id,
            result.data.status as CampaignsBulkStatusEnum,
            result.data.name,
            () => context.emit('on-close-dialog'),
          );
        } else {
          showToastError(result?.message || t('campaignBulk.create_action.error'));
          store.commit('general/hideTheSpinner');
        }
      } catch (error) {
        console.error('Campaign creation error:', error);
        showToastError(t('campaignBulk.create_action.error'));
        store.commit('general/hideTheSpinner');
      }
    };

    const handleChoose = async (record: CampaignsBulkBase) => {
      // Pre-flight: bail out if the integration isn't active on this shop.
      if (gatePreflight(record)) return;

      store.commit('general/showTheSpinner');

      try {
        const idShop = UserState.activeShop?.id ?? 0;

        if (!idShop) {
          showToastError(t('campaignBulk.create_action.error'));
          store.commit('general/hideTheSpinner');
          return;
        }

        const result = await nestCall(
          'post',
          NestApiType.DEFAULT,
          '/workflow/create-from-base',
          {},
          '',
          {
            idShop,
            type: 'campaign_bulk',
            baseId: record.path,
          },
        );

        // Backend safety net (deactivation between page load and click)
        if (gateBackendResponse(result, record)) {
          store.commit('general/hideTheSpinner');
          return;
        }

        if (result && result.success && result.id) {
          showToastSuccess(t('campaignBulk.create_action.success'));
          setRefreshScenariosList(true);
          await openBulkCampaignEditor(
            asInt(result.id),
            CampaignsBulkStatusEnum.DRAFT,
            result.name || '',
            () => context.emit('on-close-dialog'),
          );
        } else {
          showToastError(result?.message || t('campaignBulk.create_action.error'));
          store.commit('general/hideTheSpinner');
        }
      } catch (error) {
        console.error('Workflow creation error:', error);
        showToastError(t('campaignBulk.create_action.error'));
        store.commit('general/hideTheSpinner');
      }
    };

    const openGalleryHtmlPreview = async (record: CampaignsBulkBase) => {
      htmlPreviewData.value = null;
      store.commit('general/showTheSpinner');

      try {
        const result = await nestCall(
          'post',
          NestApiType.DEFAULT,
          '/workflow/gallery-html-preview',
          {},
          '',
          { path: record.path },
        );

        if (result && result.success && result.previews) {
          htmlPreviewData.value = { previews: result.previews };
        } else {
          showToastError(t('campaignBulk.workflowSelector.preview.error'));
        }
      } catch {
        showToastError(t('campaignBulk.workflowSelector.preview.error'));
      }
      store.commit('general/hideTheSpinner');
    };

    const closeHtmlPreview = () => {
      htmlPreviewData.value = null;
    };

    onMounted(async () => {
      isListLoading.value = true;

      try {
        const idShop = UserState.activeShop?.id ?? undefined;
        const result: CampaignsBulkBase[] = await getCampaignsBulkBaseList(idShop);

        if (result) {
          list.value = result;
        }
      } catch (error) {
        showToastError(t('campaignBulk.list.error'));
      } finally {
        isListLoading.value = false;
      }
    });

    // Collapse an integration's many predefined items into one collection card
    // (anti-flood). Clicking a collection drills into that integration.
    const { displayItems, drilledInIntegration } = useIntegrationCollections(
      () => filteredList.value,
      selectedAuthors,
    );
    const drillIntoIntegration = (slug: string) => {
      selectedAuthors.value = [slug];
    };
    const clearAuthorFilter = () => {
      selectedAuthors.value = [];
    };

    return {
      t,
      filteredList,
      displayItems,
      drilledInIntegration,
      drillIntoIntegration,
      clearAuthorFilter,
      htmlPreviewData,
      isListLoading,
      searchQuery,
      closeModal,
      handleChoose,
      openGalleryHtmlPreview,
      closeHtmlPreview,
      handleStartFromScratch,
      selectedAuthors,
      authorFilterOptions,
      filteredAuthorOptions,
      authorSearchQuery,
      toggleAuthor,
      getAuthorIcon,
      selectedTags,
      channelTagGroup,
      toggleTag,
      getLocalizedText,
      requireIntegrationVisible,
      requireIntegrationData,
      requireIntegrationStatus,
    };
  },
});
</script>

<style scoped lang="scss">
// ==========================================
// Campaign selector layout (sidebar left)
// ==========================================
.campaign-selector-layout {
  display: block;
  background: #f5f7fa;
  min-height: 100%;
  padding: 1.5rem;
  padding-left: calc(280px + 3rem);
  position: relative;
}

// Left sidebar - Filters (floating style with position fixed)
.filters-sidebar {
  width: 280px;
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 16px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  display: flex;
  flex-direction: column;
  height: fit-content;
  position: fixed;
  top: calc(60px + 1.5rem);
  left: 1.5rem;
  z-index: 100;
  max-height: calc(100vh - 60px - 3rem);
  overflow-y: auto;

  &__content {
    padding: 1.5rem;
    display: flex;
    flex-direction: column;
    gap: 1.5rem;
  }
}

.filter-section {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;

  &--tags {
    gap: 0.5rem;
  }

  &__label {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    font-size: 0.75rem;
    font-weight: 600;
    color: #475569;
    text-transform: uppercase;
    letter-spacing: 0.5px;

    i {
      font-size: 1rem;
      color: #94c748;
    }

    &--small {
      font-size: 0.6875rem;
      text-transform: uppercase;
      letter-spacing: 0.5px;
    }
  }
}

// Tag filter pills
.filter-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 0.375rem;
}

.filter-tag {
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0.25rem 0.625rem;
  border-radius: 20px;
  font-size: 0.75rem;
  cursor: pointer;
  transition: all 0.2s ease;
  user-select: none;
  border: 1px solid transparent;

  &--channel {
    background: #eff6ff;
    color: #3b82f6;
    border-color: #bfdbfe;

    &:hover {
      background: #dbeafe;
    }
  }

  &--active {
    &.filter-tag--channel {
      background: #3b82f6;
      color: #ffffff;
      border-color: #3b82f6;

      .filter-tag__count {
        background: rgba(255, 255, 255, 0.25);
        color: #ffffff;
      }
    }
  }

  &__label {
    font-weight: 500;
    line-height: 1;
  }

  &__count {
    font-size: 0.625rem;
    font-weight: 600;
    background: rgba(0, 0, 0, 0.06);
    padding: 0.125rem 0.375rem;
    border-radius: 10px;
    line-height: 1;
  }
}

// Search wrapper
.search-wrapper {
  position: relative;
  width: 100%;
}

// Search input
.search-icon {
  position: absolute;
  left: 1rem;
  top: 50%;
  transform: translateY(-50%);
  color: #94c748;
  font-size: 1rem;
  pointer-events: none;
  z-index: 1;
}

.search-input {
  width: 100%;
  padding: 0.75rem 2.5rem 0.75rem 2.75rem;
  border: 1.5px solid #e2e8f0;
  border-radius: 10px;
  font-size: 0.875rem;
  color: #1e293b;
  background: #f8fafc;
  transition: all 0.2s ease;
  outline: none;

  &::placeholder {
    color: #94a3b8;
  }

  &:hover {
    border-color: #cbd5e1;
    background: #ffffff;
  }

  &:focus {
    border-color: #94c748;
    background: #ffffff;
    box-shadow: 0 0 0 4px rgba(148, 199, 72, 0.12);
  }
}

.clear-search-btn {
  position: absolute;
  right: 0.75rem;
  top: 50%;
  transform: translateY(-50%);
  background: #e2e8f0;
  border: none;
  border-radius: 6px;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
  padding: 0;
  color: #64748b;

  &:hover {
    background: #94c748;
    color: #ffffff;
  }

  i {
    font-size: 0.5625rem;
  }
}

// ============ Author filter — visual card list ============
// Same UX as in CampaignAutomationSelectorV2: ShopiMind row first, then
// an optional partner sub-section with an inline search + scrollable
// list once the partner count exceeds 5.
.author-list {
  display: flex;
  flex-direction: column;
  gap: 0.35rem;

  &--scrollable {
    max-height: 220px;
    overflow-y: auto;
    margin-top: 0.35rem;
    padding-right: 2px;
    &::-webkit-scrollbar {
      width: 6px;
    }
    &::-webkit-scrollbar-thumb {
      background: #d1d5db;
      border-radius: 9999px;
    }
    &::-webkit-scrollbar-thumb:hover {
      background: #9ca3af;
    }
  }

  &__subheader {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.5rem;
    margin: 0.75rem 0.25rem 0.35rem;
    padding-top: 0.6rem;
    border-top: 1px solid #f3f4f6;
    font-size: 0.65rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    color: #6b7280;
  }

  &__subcount {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 18px;
    padding: 1px 6px;
    border-radius: 9999px;
    background: #ede9fe;
    color: #6b21a8;
    font-size: 0.6rem;
    font-weight: 700;
    letter-spacing: 0;
    text-transform: none;
  }

  &__search {
    position: relative;
    margin-bottom: 0.35rem;

    i {
      position: absolute;
      top: 50%;
      left: 0.55rem;
      transform: translateY(-50%);
      color: #9ca3af;
      font-size: 0.75rem;
      pointer-events: none;
    }

    input {
      width: 100%;
      padding: 0.4rem 0.5rem 0.4rem 1.8rem;
      border: 1px solid #e5e7eb;
      border-radius: 8px;
      font-size: 0.8rem;
      background: #ffffff;
      transition: border-color 0.15s ease;

      &:focus {
        outline: none;
        border-color: #c4b5fd;
        box-shadow: 0 0 0 3px rgba(139, 92, 246, 0.12);
      }
    }
  }

  &__empty {
    padding: 0.8rem 0.5rem;
    text-align: center;
    color: #9ca3af;
    font-size: 0.75rem;
    font-style: italic;
  }

  &__item {
    display: flex;
    align-items: center;
    gap: 0.6rem;
    padding: 0.5rem 0.6rem;
    border: 1.5px solid transparent;
    border-radius: 8px;
    background: #ffffff;
    cursor: pointer;
    transition: background 0.15s ease, border-color 0.15s ease;

    &:hover {
      background: #f8fafc;
      border-color: #e2e8f0;
    }

    &--active {
      background: #f5f3ff;
      border-color: #c4b5fd;
    }

    &--integration:not(.author-list__item--active) {
      background: linear-gradient(90deg, #faf5ff 0%, #ffffff 12%);
    }

    &--integration.author-list__item--active {
      background: #ede9fe;
      border-color: #8b5cf6;
    }
  }

  &__avatar {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 28px;
    border-radius: 9999px;
    background: #ffffff;
    border: 1.5px solid #e5e7eb;
    overflow: hidden;
    flex-shrink: 0;

    img {
      width: 18px;
      height: 18px;
      object-fit: contain;
      display: block;
    }

    i {
      font-size: 0.85rem;
      color: #6b7280;
    }
  }

  &__item--integration .author-list__avatar {
    border-color: #c4b5fd;

    i {
      color: #7c3aed;
    }
  }

  &__item--active .author-list__avatar {
    border-color: #8b5cf6;
  }

  &__label {
    flex: 1;
    min-width: 0;
    font-size: 0.85rem;
    font-weight: 500;
    color: #374151;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__count {
    flex-shrink: 0;
    min-width: 20px;
    height: 18px;
    padding: 0 6px;
    border-radius: 9999px;
    background: #ece9fb;
    color: #6d28d9;
    font-size: 0.7rem;
    font-weight: 700;
    display: inline-flex;
    align-items: center;
    justify-content: center;
  }

  &__item--active &__label {
    font-weight: 600;
    color: #1f2937;
  }

  &__check {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 18px;
    height: 18px;
    border-radius: 9999px;
    background: #8b5cf6;
    color: #ffffff;
    font-size: 0.55rem;
    flex-shrink: 0;
  }

  &__item--active:not(.author-list__item--integration) &__check {
    background: #94c748;
  }
}

// Legacy MultiSelect styling (kept in case it's still imported elsewhere).
.author-multiselect {
  width: 100%;

  :deep(.p-multiselect) {
    width: 100%;
    border-radius: 8px;
    border: 1.5px solid #e0e0e0;
    background: #ffffff;

    &:hover {
      border-color: #bdbdbd;
    }

    &.p-focus {
      border-color: #94c748;
      box-shadow: 0 0 0 3px rgba(148, 199, 72, 0.1);
    }

    .p-multiselect-label {
      padding: 0.5rem 0.75rem;
      font-size: 0.8125rem;
    }
  }
}

// Results count
.filter-results {
  display: flex;
  align-items: center;
  gap: 0.625rem;
  padding: 1rem 1.25rem;
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
  border-radius: 12px;
  border: 1px solid #e2e8f0;
  margin-top: 0.5rem;

  &__count {
    font-size: 1.5rem;
    font-weight: 700;
    color: #94c748;
    line-height: 1;
  }

  &__label {
    font-size: 0.8125rem;
    color: #64748b;
    font-weight: 500;
  }
}

// Campaigns content
.campaigns-content {
  flex: 1;
  padding: 0;
  background: transparent;
}

.campaigns-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
  gap: 1.25rem;
  animation: fadeIn 0.4s ease-in-out;
}

// Campaign card
.campaign-card {
  position: relative;
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  /* `overflow: visible` so the partner ribbon can hang outside (same
     pattern as the automation picker). The image gets its own clipping. */
  overflow: visible;
  transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
  cursor: pointer;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.1);
    border-color: #94c748;
  }

  /* Integration variant — same treatment as in CampaignAutomationSelectorV2. */
  &--integration {
    background: linear-gradient(180deg, #faf5ff 0%, #ffffff 38%);
    border: 1.5px solid #c4b5fd;
    box-shadow: 0 1px 3px rgba(139, 92, 246, 0.08);

    &:hover {
      border-color: #8b5cf6;
      box-shadow: 0 10px 28px rgba(139, 92, 246, 0.22);
    }
  }

  /* Partner ribbon overhanging the top-left corner. z-index 20 sits
     above the hover overlay (z-index 10) so the partner branding stays
     visible while the user inspects the card's description. */
  &__partner-ribbon {
    position: absolute;
    top: -12px;
    left: -10px;
    z-index: 20;
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    padding: 5px 12px 5px 6px;
    background: #ffffff;
    border: 1.5px solid #c4b5fd;
    border-radius: 9999px;
    box-shadow: 0 6px 16px rgba(91, 33, 182, 0.22);
    pointer-events: none;
  }

  &__partner-logo {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 26px;
    height: 26px;
    border-radius: 9999px;
    background: #ffffff;
    border: 1.5px solid #c4b5fd;
    overflow: hidden;
    flex-shrink: 0;

    img {
      width: 16px;
      height: 16px;
      object-fit: contain;
      display: block;
    }

    i {
      font-size: 0.75rem;
      color: #7c3aed;
    }
  }

  &__partner-text {
    display: flex;
    flex-direction: column;
    line-height: 1.1;
    min-width: 0;
  }

  &__partner-kicker {
    font-size: 0.55rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    color: #7c3aed;
  }

  &__partner-name {
    font-size: 0.75rem;
    font-weight: 600;
    color: #1f2937;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    max-width: 140px;
  }

  &__content {
    position: relative;
    display: flex;
    flex-direction: column;
    height: 100%;
  }

  &__image {
    position: relative;
    width: 100%;
    height: 180px;
    /* Round the top corners explicitly — the card has lost its
       overflow: hidden so we re-clip the preview image only. */
    border-top-left-radius: 12px;
    border-top-right-radius: 12px;
    background: #f8f8f8;
    overflow: hidden;

    &--scratch {
      display: flex;
      align-items: center;
      justify-content: center;
      background: #f8fafc;
      border-bottom: 1px solid #e2e8f0;

      .scratch-icon-wrapper {
        width: 56px;
        height: 56px;
        border-radius: 50%;
        background: #ffffff;
        border: 2px dashed #cbd5e1;
        display: flex;
        align-items: center;
        justify-content: center;
        transition: all 0.3s ease;
      }

      i {
        font-size: 1.25rem;
        color: #94a3b8;
        transition: all 0.3s ease;
      }

      .campaign-card:hover & .scratch-icon-wrapper {
        border-color: #94c748;
        border-style: solid;
        background: #f0fdf4;
      }

      .campaign-card:hover & i {
        color: #94c748;
      }
    }
  }

  &__preview {
    width: 100%;
    height: 100%;
    background-size: 100% auto;
    background-position: center top;
    background-repeat: no-repeat;
    transition: background-position 3s ease-in-out;

    &--placeholder {
      display: flex;
      align-items: center;
      justify-content: center;
      background: linear-gradient(135deg, #fafafa 0%, #f0f0f0 100%);

      i {
        font-size: 2.5rem;
        color: #bdbdbd;
      }
    }

    .campaign-card:hover & {
      background-position: center bottom;
    }
  }

  &__body {
    padding: 1rem;
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 0.375rem;
  }

  &__title {
    font-size: 0.9375rem;
    font-weight: 600;
    color: #1f2937;
    margin: 0;
    line-height: 1.3;
    min-height: calc(2 * 1.3em);
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }

  &__author {
    display: flex;
    align-items: center;
    gap: 0.375rem;
    font-size: 0.75rem;
    color: #64748b;
    margin-top: auto;
    padding-top: 0.5rem;

    i {
      font-size: 0.6875rem;
      color: #94a3b8;
    }
  }

  // Hover overlay
  &__hover-overlay {
    position: absolute;
    inset: 0;
    /* Match the card radius — the card is `overflow: visible` (for the ribbon),
       so without this the square overlay corners poke past the rounded border. */
    border-radius: 12px;
    background: rgba(255, 255, 255, 0.97);
    display: flex;
    flex-direction: column;
    padding: 1.25rem;
    opacity: 0;
    transition: opacity 0.25s ease;
    pointer-events: none;
    z-index: 3;
    overflow-y: auto;
  }

  &:hover &__hover-overlay {
    opacity: 1;
    pointer-events: auto;
  }

  &__hover-legend {
    margin: 0 0 0.75rem;
    font-size: 0.8125rem;
    line-height: 1.5;
    color: #475569;
    flex: 1;
    display: -webkit-box;
    -webkit-line-clamp: 5;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }

  &__hover-tags {
    display: flex;
    flex-wrap: wrap;
    gap: 0.375rem;
    margin-bottom: 0.75rem;
  }

  &__hover-actions {
    display: flex;
    gap: 0.5rem;
    margin-top: auto;

    :deep(.p-button) {
      font-size: 0.8125rem;
      padding: 0.5rem 0.75rem;
      border-radius: 6px;

      &.preview-btn {
        width: 36px;
        min-width: 36px;
        justify-content: center;
        border-color: #e2e8f0;
        color: #64748b;

        &:hover {
          background: #f1f5f9;
          border-color: #cbd5e1;
        }
      }

      &.choose-btn {
        background: #94c748;
        border-color: #94c748;
        font-weight: 500;
        flex: 1;
        justify-content: center;
        color: white;

        &:hover {
          background: #82b33d;
          border-color: #82b33d;
        }
      }
    }
  }

  // Actions for scratch card only
  &__actions {
    padding: 0.75rem 1rem;
    border-top: 1px solid #f3f4f6;
    display: flex;
    gap: 0.5rem;
    background: #fafafa;

    :deep(.p-button) {
      font-size: 0.8125rem;
      padding: 0.5rem 0.75rem;
      border-radius: 6px;

      &.choose-btn {
        background: #94c748;
        border-color: #94c748;
        font-weight: 500;
        flex: 1;
        justify-content: center;
        color: white;

        &:hover {
          background: #82b33d;
          border-color: #82b33d;
        }
      }
    }
  }

  // Special styling for scratch card
  &--scratch {
    border: 1px solid #e5e7eb;
    background: #ffffff;

    .campaign-card__body {
      text-align: center;
    }

    .campaign-card__description {
      color: #94a3b8;
    }
  }
}

// Tag badges in hover overlay
.tag-badge {
  display: inline-flex;
  align-items: center;
  padding: 0.125rem 0.5rem;
  border-radius: 12px;
  font-size: 0.6875rem;
  font-weight: 500;
  line-height: 1.4;

  &--channel {
    background: #eff6ff;
    color: #3b82f6;
  }
}

// Empty state
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 4rem 2rem;
  color: #9ca3af;
  text-align: center;

  i {
    font-size: 4rem;
    margin-bottom: 1rem;
    opacity: 0.5;
  }

  p {
    font-size: 1rem;
    margin: 0;
  }
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

// ==========================================
// Responsive
// ==========================================
@media (max-width: 900px) {
  .campaign-selector-layout {
    display: block;
    padding: 1.25rem;
    padding-left: 1.25rem;
  }

  .filters-sidebar {
    width: 100%;
    position: relative;
    top: 0;
    left: 0;
    max-height: none;
    margin-bottom: 1.25rem;

    &__content {
      flex-direction: row;
      flex-wrap: wrap;
      gap: 1.25rem;
      padding: 1.25rem;
    }
  }

  .filter-section {
    flex: 1;
    min-width: 200px;
  }

  .filter-results {
    margin-top: 0;
    flex: 0 0 auto;
  }

  .campaigns-grid {
    grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
    gap: 1.25rem;
  }
}

@media (max-width: 600px) {
  .filters-sidebar__content {
    flex-direction: column;
  }

  .filter-section {
    min-width: 100%;
  }

  .campaigns-content {
    padding: 1rem;
  }

  .campaigns-grid {
    grid-template-columns: 1fr;
  }

  .campaign-card__image {
    height: 160px;
  }
}
</style>

<style lang="scss">
// Override EditorModal styles when campaign selector is open
.content.bg-white:has(.campaign-selector-layout) {
  background: #f5f7fa !important;
}
</style>
